/**
 * InteractionBus - Lightweight pub/sub event emitter for cross-chart communication
 * 
 * Enables charts to broadcast and listen to highlight/filter events without
 * direct coupling between components.
 */

export type InteractionEventType = 'highlight' | 'filter' | 'clear';

export interface HighlightPayload {
    sourceChartId: string;
    field: string;
    values: any[];
}

export interface FilterPayload {
    sourceChartId: string;
    field: string;
    values: any[];
}

export interface ClearPayload {
    sourceChartId?: string;
    clearType: 'highlight' | 'filter' | 'all';
}

export type InteractionPayload = HighlightPayload | FilterPayload | ClearPayload;

type EventCallback = (payload: InteractionPayload) => void;

class InteractionBusClass {
    private listeners: Map<InteractionEventType, Set<EventCallback>> = new Map();

    /**
     * Subscribe to an interaction event
     */
    on(eventType: InteractionEventType, callback: EventCallback): () => void {
        if (!this.listeners.has(eventType)) {
            this.listeners.set(eventType, new Set());
        }
        this.listeners.get(eventType)!.add(callback);

        // Return unsubscribe function
        return () => this.off(eventType, callback);
    }

    /**
     * Unsubscribe from an interaction event
     */
    off(eventType: InteractionEventType, callback: EventCallback): void {
        const callbacks = this.listeners.get(eventType);
        if (callbacks) {
            callbacks.delete(callback);
        }
    }

    /**
     * Emit an interaction event to all subscribers
     */
    emit(eventType: InteractionEventType, payload: InteractionPayload): void {
        const callbacks = this.listeners.get(eventType);
        if (callbacks) {
            callbacks.forEach(callback => {
                try {
                    callback(payload);
                } catch (error) {
                    console.error(`[InteractionBus] Error in ${eventType} handler:`, error);
                }
            });
        }
    }

    /**
     * Clear all listeners (useful for cleanup)
     */
    reset(): void {
        this.listeners.clear();
    }

    /**
     * Get subscriber count for debugging
     */
    getSubscriberCount(eventType: InteractionEventType): number {
        return this.listeners.get(eventType)?.size || 0;
    }
}

// Singleton instance
export const InteractionBus = new InteractionBusClass();
